import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthContext } from '../../../context/AuthContext';
import { SOLUTIONS } from '../../pr-compliance/constants';
import { PrCompRunConfig } from '../../pr-compliance/types';
import { EvalApiService } from '../../services/EvalApiService';
import { EvalGtDataset, EvalModel, EvalRun, RunType } from '../../types';
import { fmtIso } from '../../utils';

const RUN_TYPES: RunType[] = ['Full', 'OD', 'PR', 'PC', 'PR_Compliance'];

const RUN_TYPE_LABELS: Record<RunType, string> = {
  Full: 'Full',
  OD: 'OD',
  PR: 'PR',
  PC: 'PC',
  PR_Compliance: 'PR Compliance',
};

// Which run types can serve as base run input for a given run type
const COMPATIBLE_SOURCE_TYPES: Record<'PR' | 'PC', RunType[]> = {
  PR: ['OD', 'Full'],
  PC: ['PR', 'Full'],
};

// The model most relevant to the next stage: OD output feeds PR; PR output feeds PC.
// Returns the resolved service_model_id when available (falls back to mongo _id).
function keyModelLabel(run: EvalRun, forRunType: 'PR' | 'PC'): string | null {
  const layer = forRunType === 'PR' ? 'OD' : 'VE';
  return run.model_info[layer]?.model_name ?? run.model_ids[layer];
}

function baseRunLabel(run: EvalRun, forRunType: 'PR' | 'PC'): string {
  const date = fmtIso(run.triggered_at);
  const model = keyModelLabel(run, forRunType);
  return `${date} · [${run.run_type}]${model ? ` · ${model}` : ''}`;
}

export const RunSchedulerPage: React.FC = () => {
  const { token } = useAuthContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [models, setModels] = useState<EvalModel[]>([]);
  const [datasets, setDatasets] = useState<EvalGtDataset[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // ?solution=pr_compliance in the URL pre-selects the PR Compliance run type.
  const [runType, setRunType] = useState<RunType>(() =>
    searchParams.get('solution') === 'pr_compliance' ? 'PR_Compliance' : 'Full'
  );
  const [odModelId, setOdModelId] = useState<string>('');
  const [prModelId, setPrModelId] = useState<string>('');
  const [pcModelId, setPcModelId] = useState<string>('');
  const [datasetId, setDatasetId] = useState<string>('');
  const [configJson, setConfigJson] = useState<string>('');

  // PR Compliance solutions (only relevant when runType === 'PR_Compliance').
  // Both default on because the point of this run type is comparing the two.
  const [runPcV2, setRunPcV2] = useState(true);
  const [runLegacy, setRunLegacy] = useState(true);

  // Base run state (only relevant for standalone PR and PC run types)
  const [useBaseRun, setUseBaseRun] = useState(false);
  const [baseRunId, setBaseRunId] = useState<string>('');
  const [baseRuns, setBaseRuns] = useState<EvalRun[]>([]);
  const [baseRunsLoading, setBaseRunsLoading] = useState(false);

  const cloneFromId = searchParams.get('from');
  const [cloneFailed, setCloneFailed] = useState(false);

  // Prefill the form from an existing run when cloning. Everything stays editable.
  const applyClonePrefill = (src: EvalRun) => {
    const solutionType = src.config?.solution_type;
    if (solutionType === 'pc_v2') {
      // Stored as a Full run; the PR Compliance distinction lives in config.solution_type.
      setRunType('PR_Compliance');
      setRunPcV2(true);
      setRunLegacy(false);
    } else if (solutionType === 'legacy_match_oos') {
      setRunType('PR_Compliance');
      setRunPcV2(false);
      setRunLegacy(true);
    } else {
      setRunType(src.run_type);
      if (src.config && Object.keys(src.config).length > 0) {
        setConfigJson(JSON.stringify(src.config, null, 2));
      }
    }
    setOdModelId(src.model_ids.OD ?? '');
    setPrModelId(src.model_ids.VE ?? '');
    setPcModelId(src.model_ids.PC ?? '');
    setDatasetId(src.dataset_id);
    if (src.source_run_id) {
      setUseBaseRun(true);
      setBaseRunId(src.source_run_id);
    }
  };

  // Initial load — the clone source (if any) is fetched here, before the form
  // renders, so the prefill needs no coordination with the effects below.
  useEffect(() => {
    Promise.all([
      EvalApiService.listModels(token ?? ''),
      EvalApiService.listDatasets(token ?? ''),
      cloneFromId
        ? EvalApiService.getRun(token ?? '', cloneFromId).catch(() => null)
        : Promise.resolve(null),
    ])
      .then(([{ items: m }, { items: d }, cloneSource]) => {
        setModels(m);
        const sorted = [...d].sort((a, b) => {
          if (a.status === 'locked' && b.status !== 'locked') return -1;
          if (a.status !== 'locked' && b.status === 'locked') return 1;
          return 0;
        });
        setDatasets(sorted);
        if (cloneSource) applyClonePrefill(cloneSource);
        // Deleted source run, bad ?from id, or fetch failure — surface it
        // instead of silently showing a default form.
        if (cloneFromId && !cloneSource) setCloneFailed(true);
      })
      .finally(() => setLoading(false));
  }, [token, cloneFromId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Base run selection only makes sense for the dataset/run-type it was picked
  // for — reset it on user-initiated changes of either.
  const resetBaseRun = () => {
    setUseBaseRun(false);
    setBaseRunId('');
    setBaseRuns([]);
  };

  // Load eligible base runs when the toggle is on; keep the current selection
  // unless it turns out to be ineligible (e.g. a cloned source run was deleted).
  useEffect(() => {
    if (!useBaseRun || !datasetId || (runType !== 'PR' && runType !== 'PC')) return;
    setBaseRunsLoading(true);
    const compatibleTypes = COMPATIBLE_SOURCE_TYPES[runType];
    EvalApiService.listRuns(token ?? '', { dataset_id: datasetId, status: 'completed' }).then(
      ({ items }) => {
        const eligible = items.filter((r) => compatibleTypes.includes(r.run_type));
        setBaseRuns(eligible);
        setBaseRunId((prev) => (prev && !eligible.some((r) => r.run_id === prev) ? '' : prev));
        setBaseRunsLoading(false);
      }
    );
  }, [useBaseRun, datasetId, runType, token]);

  const selectedDataset = datasets.find((d) => d.dataset_id === datasetId);
  const isDraft = selectedDataset?.status === 'draft';

  const isPrComp = runType === 'PR_Compliance';
  // In PR Compliance mode, model pickers are only relevant to the PC v2 solution;
  // legacy is a SQL import that needs no models.
  const showOD = isPrComp ? runPcV2 : runType === 'Full' || runType === 'OD';
  const showPR = isPrComp ? runPcV2 : runType === 'Full' || runType === 'PR';
  const showPC = isPrComp ? runPcV2 : runType === 'Full' || runType === 'PC';
  // Toggle is only available for standalone PR and PC runs
  const showBaseRunToggle = runType === 'PR' || runType === 'PC';

  const odModels = models.filter((m) => m.model_type === 'OD' && !m.is_archived);
  const prModels = models.filter((m) => m.model_type === 'VE' && !m.is_archived);
  const pcModels = models.filter((m) => m.model_type === 'PC' && !m.is_archived);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!datasetId) return;
    if (isPrComp && !runPcV2 && !runLegacy) return;
    setSubmitting(true);
    try {
      if (isPrComp) {
        // Run whichever solutions are checked, then open Compare with all of them.
        const ids: string[] = [];
        // Legacy import requires a locked dataset; lock first (also fine for PC v2).
        if (runLegacy && isDraft) {
          await EvalApiService.lockDataset(token ?? '', datasetId);
        }
        if (runPcV2) {
          // Bleedover / sequence toggles were removed from the UI for now; send defaults.
          const prCompConfig: PrCompRunConfig = {
            solution_type: 'pc_v2',
            bleedover_enabled: true,
            sequence_mode: false,
          };
          const pcRun = await EvalApiService.scheduleRun(token ?? '', {
            // 'PR_Compliance' is a UI-only run type; the backend accepts Full/OD/PR/PC.
            // The PR Compliance distinction is carried in config.solution_type.
            run_type: 'Full',
            model_ids: {
              OD: odModelId || null,
              VE: prModelId || null,
              PC: pcModelId || null,
            },
            dataset_id: datasetId,
            source_run_id: null,
            config: prCompConfig as unknown as Record<string, unknown>,
          });
          if (pcRun.run_id) ids.push(pcRun.run_id);
        }
        if (runLegacy) {
          const legacyRuns = await EvalApiService.importLegacyRun(token ?? '', datasetId);
          ids.push(...legacyRuns.map((r) => r.run_id).filter((id): id is string => Boolean(id)));
        }
        navigate(ids.length ? `/eval/runs/compare?runs=${ids.join(',')}` : '/eval/runs');
        return;
      }

      let parsedConfig: Record<string, unknown> = {};
      if (configJson.trim()) {
        parsedConfig = JSON.parse(configJson);
      }
      const newRun = await EvalApiService.scheduleRun(token ?? '', {
        run_type: runType,
        model_ids: {
          OD: showOD ? odModelId || null : null,
          VE: showPR ? prModelId || null : null,
          PC: showPC ? pcModelId || null : null,
        },
        dataset_id: datasetId,
        source_run_id: showBaseRunToggle && useBaseRun ? baseRunId || null : null,
        config: parsedConfig,
      });
      navigate(`/eval/runs/${newRun.run_id}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="page-content">
        <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Loading...</div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="eval-page-header">
        <div>
          <div className="eval-page-title">
            {isPrComp ? 'Schedule PR Compliance run' : 'Schedule Evaluation Run'}
          </div>
          <div className="eval-page-sub">
            {isPrComp
              ? 'Score a pipeline (legacy or new) against a labelled dataset. Compare two completed runs on the Compare page.'
              : 'Configure and launch a new evaluation run against a ground-truth dataset.'}
          </div>
          {cloneFromId &&
            (cloneFailed ? (
              <div style={{ fontSize: 12, color: 'var(--warning)', marginTop: 4 }}>
                Could not load run{' '}
                <span style={{ fontFamily: 'var(--font-mono)' }}>{cloneFromId}</span> — the form was
                not pre-filled.
              </div>
            ) : (
              <div style={{ fontSize: 12, color: 'var(--accent)', marginTop: 4 }}>
                Pre-filled from run{' '}
                <span style={{ fontFamily: 'var(--font-mono)' }}>{cloneFromId}</span> — adjust
                anything before scheduling.
              </div>
            ))}
        </div>
      </div>

      <form className="eval-form" onSubmit={handleSubmit}>
        <div className="eval-form-group">
          <label className="eval-form-label">Run Type</label>
          <select
            className="eval-form-select"
            value={runType}
            onChange={(e) => {
              setRunType(e.target.value as RunType);
              resetBaseRun();
            }}
          >
            {RUN_TYPES.map((t) => (
              <option key={t} value={t}>
                {RUN_TYPE_LABELS[t]}
              </option>
            ))}
          </select>
        </div>

        {isPrComp && (
          <div className="eval-form-group">
            <label className="eval-form-label">Solutions to run</label>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                padding: '12px 14px',
                background: 'var(--surface-raised)',
                border: '1px solid var(--border)',
                borderRadius: 6,
              }}
            >
              {SOLUTIONS.map((s) => {
                const checked = s.value === 'pc_v2' ? runPcV2 : runLegacy;
                const setChecked = s.value === 'pc_v2' ? setRunPcV2 : setRunLegacy;
                return (
                  <label
                    key={s.value}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 10,
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => setChecked(e.target.checked)}
                      style={{ marginTop: 3 }}
                    />
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{s.label}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                        {s.description}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
            {!runPcV2 && !runLegacy && (
              <div className="eval-form-warning">Select at least one solution to run.</div>
            )}
          </div>
        )}

        {showOD && (
          <div className="eval-form-group">
            <label className="eval-form-label">OD Model</label>
            <select
              className="eval-form-select"
              value={odModelId}
              onChange={(e) => setOdModelId(e.target.value)}
            >
              <option value="">— Select OD model —</option>
              {odModels.map((m) => (
                <option key={m.model_id} value={m.model_id}>
                  {m.model_name}
                  {m.is_archived ? ' (archived)' : ''}
                </option>
              ))}
            </select>
          </div>
        )}

        {showPR && (
          <div className="eval-form-group">
            <label className="eval-form-label">PR Model</label>
            <select
              className="eval-form-select"
              value={prModelId}
              onChange={(e) => setPrModelId(e.target.value)}
            >
              <option value="">— Select PR model —</option>
              {prModels.map((m) => (
                <option key={m.model_id} value={m.model_id}>
                  {m.model_name}
                  {m.is_archived ? ' (archived)' : ''}
                </option>
              ))}
            </select>
          </div>
        )}

        {showPC && (
          <div className="eval-form-group">
            <label className="eval-form-label">PC Model</label>
            <select
              className="eval-form-select"
              value={pcModelId}
              onChange={(e) => setPcModelId(e.target.value)}
            >
              <option value="">— Select PC model —</option>
              {pcModels.map((m) => (
                <option key={m.model_id} value={m.model_id}>
                  {m.model_name}
                  {m.is_archived ? ' (archived)' : ''}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="eval-form-group">
          <label className="eval-form-label">Dataset</label>
          <select
            className="eval-form-select"
            value={datasetId}
            onChange={(e) => {
              setDatasetId(e.target.value);
              resetBaseRun();
            }}
            required
          >
            <option value="">— Select dataset —</option>
            {datasets.map((d) => (
              <option key={d.dataset_id} value={d.dataset_id}>
                {d.dataset_name} {d.status === 'locked' ? '(locked)' : '(draft)'} — {d.sample_count}{' '}
                samples
              </option>
            ))}
          </select>
          {isDraft && (
            <div className="eval-form-warning">
              Warning: selected dataset is in draft status and may have incomplete annotations.
            </div>
          )}
        </div>

        {!isPrComp && showBaseRunToggle && datasetId && (
          <div className="eval-form-group">
            <label className="eval-form-label">Input Data Source</label>
            <div style={{ display: 'flex', gap: 8, marginBottom: useBaseRun ? 10 : 0 }}>
              <button
                type="button"
                className={useBaseRun ? 'eval-btn-ghost' : 'eval-btn'}
                style={{ padding: '5px 14px', fontSize: 12 }}
                onClick={() => setUseBaseRun(false)}
              >
                Use GT data
              </button>
              <button
                type="button"
                className={useBaseRun ? 'eval-btn' : 'eval-btn-ghost'}
                style={{ padding: '5px 14px', fontSize: 12 }}
                onClick={() => setUseBaseRun(true)}
              >
                Use base run
              </button>
            </div>
            {useBaseRun &&
              (baseRunsLoading ? (
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  Loading eligible runs...
                </div>
              ) : baseRuns.length === 0 ? (
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  No completed base runs available for this dataset.
                </div>
              ) : (
                <select
                  className="eval-form-select"
                  value={baseRunId}
                  onChange={(e) => setBaseRunId(e.target.value)}
                >
                  <option value="">— Select base run —</option>
                  {baseRuns.map((r) => (
                    <option key={r.run_id} value={r.run_id}>
                      {baseRunLabel(r, runType as 'PR' | 'PC')}
                    </option>
                  ))}
                </select>
              ))}
          </div>
        )}

        {!isPrComp && (
          <div className="eval-form-group">
            <label className="eval-form-label">Config (optional JSON)</label>
            <textarea
              className="eval-form-textarea"
              value={configJson}
              onChange={(e) => setConfigJson(e.target.value)}
              placeholder='{"iou_threshold": 0.5, "confidence_threshold": 0.3}'
            />
          </div>
        )}

        <div>
          <button
            type="submit"
            className="eval-btn"
            disabled={submitting || !datasetId || (isPrComp && !runPcV2 && !runLegacy)}
          >
            {submitting
              ? 'Working…'
              : isPrComp && runPcV2 && runLegacy
                ? 'Run & compare'
                : isPrComp && runLegacy && !runPcV2
                  ? 'Import Core Model runs'
                  : 'Schedule Run'}
          </button>
          {isPrComp && runLegacy && (
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 8 }}>
              Core Model adds two runs (low scored as in-stock and as out) imported from SQL.
              {runPcV2 && ' PC v2 is scheduled on Ray and fills in once it completes.'}
              {isDraft && ' The draft dataset will be locked first.'} Opens Compare with all runs.
            </div>
          )}
        </div>
      </form>
    </div>
  );
};
